// API Response Types - Shared between client and server

import type { CurationStatus } from '@packages/enums';
import type { Record, DiagnosisItem } from './patient';

// Pagination types
export interface PaginatedResponse<T> {
    records: T[];
    total: number;
    hasNext: boolean;
    page: number;
    pageSize: number;
}

// Patient Events Response
export interface PatientEventsResponse {
    records: PatientEventRecord[];
    total: number;
    hasNext: boolean;
    page: number;
    pageSize: number;
}

// Patient Events Query Request
export enum SortOrder {
    ASC = 'asc',
    DESC = 'desc',
}

export enum PatientEventSortField {
    START_DATE = 'start_date',
    END_DATE = 'end_date',
    DETAIL_CONCEPT_NAME = 'detail_concept_name',
    DOMAIN_ID = 'domain_id',
    DATA_SOURCE = 'data_source',
    OMOP_ID = 'omop_id',
}

export enum ProcedureSortField {
    PROCEDURE_DATE = 'procedure_date',
    CONCEPT_NAME = 'concept_name',
    PROCEDURE_SOURCE_VALUE = 'procedure_source_value',
    PROCEDURE_OCCURRENCE_ID = 'procedure_occurrence_id',
}

export enum ConditionSortField {
    CONDITION_START_DATE = 'condition_start_date',
    CONDITION_END_DATE = 'condition_end_date',
    CONCEPT_NAME = 'concept_name',
    CONDITION_SOURCE_VALUE = 'condition_source_value',
    CONDITION_OCCURRENCE_ID = 'condition_occurrence_id',
}

export enum MedicationSortField {
    DRUG_EXPOSURE_START_DATE = 'drug_exposure_start_date',
    DRUG_EXPOSURE_END_DATE = 'drug_exposure_end_date',
    CONCEPT_NAME = 'concept_name',
    DRUG_SOURCE_VALUE = 'drug_source_value',
    DRUG_EXPOSURE_ID = 'drug_exposure_id',
}

export enum VisitSortField {
    VISIT_START_DATE = 'visit_start_date',
    VISIT_END_DATE = 'visit_end_date',
    VISIT_CONCEPT_NAME = 'visit_concept_name',
    VISIT_SOURCE_VALUE = 'visit_source_value',
    VISIT_OCCURRENCE_ID = 'visit_occurrence_id',
    CARE_SITE_NAME = 'care_site_name',
}

// Patient Visits Response
export interface PatientVisitsResponse {
    records: PatientVisitRecord[];
    total: number;
    hasNext: boolean;
    page: number;
    pageSize: number;
}

// Patient Visit Record
export interface PatientVisitRecord {
    visit_occurrence_id: number;
    person_id: number;
    visit_concept_name: string | null;
    visit_type_name: string | null;
    visit_start_date: string | null;
    visit_end_date: string | null;
    visit_source_value: string | null;
    care_site_id: number | null;
    care_site_name: string | null;
    provider_id: number | null;
    admitted_from: string | null;
    discharged_to: string | null;
    person_source_value: string | null;
}

export interface PatientEventsQueryRequest {
    patient_id: string;
    page?: number;
    page_size?: number;
    sort_by?: string;
    sort_order?: SortOrder;
    start_date?: string;
    end_date?: string;
    request_type?: string;
}

export interface PatientEventsQueryResponse {
    records: any[];
    total_records: number;
    has_next: boolean;
    page: number;
    page_size: number;
    patient_id?: string;
    sort_by?: string;
    sort_order?: SortOrder;
    start_date?: string;
    end_date?: string;
}

// Patient Response (single patient)
export interface PatientResponse {
    id: string;
    name: string;
    age: number;
    gender: string;
    race?: string;
    documentCount?: number;
    ethnicity?: string;
    birthDate?: string;
    /** Structured diagnosis list with ICD codes and dates, sorted newest first */
    diagnosis_list?: DiagnosisItem[];
    /** @deprecated Use diagnosis_list instead */
    diagnosis: string[];
    /** @deprecated Use diagnosis_list instead */
    diagnosisIcdCodes?: string[];
    records: Record[];
    location?: {
        city?: string;
        state?: string;
        zip?: string;
        county?: string;
    };
    provider?: {
        name?: string;
        specialty?: string;
    };
    careSite?: {
        name?: string;
        placeOfService?: string;
    };
    noteCount?: number;
    omopPersonId?: string; // OMOP person_id when matched via person_source_value
    sourcePatientId?: string; // elasticsearch source patient id
    /** Where this patient was found: omop=condition_occurrence, es=raw_nlp_annotations_enriched (NLP) */
    sourceFlags?: ('omop' | 'es' | 'es-only')[];
    /** True when patient exists only in ES (no OMOP person record). Used for cohort_membership.is_es_only */
    isEsOnly?: boolean;
}

// Patients List Response
export interface PatientsResponse {
    patients: PatientResponse[];
    total: number;
    page?: number;
    pageSize?: number;

    randomSeed?: number;

    totalDocumentCount?: number; // Total count of documents matching documentFilter (without excludedIds)
}

// Curations Response
export interface CurationsResponse {
    id: string;
    name: string;
    status: CurationStatus;
    progress: number;
    createdAt: string;
    cohortList: string[];
    selectionType: 'patient' | 'record';
    patientCount?: number;
    recordCount?: number;
    ontologyMethod: string;
}

// Documents Response
export interface DocumentsResponse {
    documents: Record[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
}

export interface PatientEventRecord {
    omop_id: number;
    detail_concept_name: string;
    detail_concept_name_lower: string;
    data_source: string;
    detail_concept_id: number;
    person_id: number;
    person_source_value: string;
    domain_id: string;
    table_name: string;
    column_concept_id: number;
    value_as_number: any;
    value_as_concept_id: number;
    value_concept_name: string;
    chapter: any;
    value_as_string: any;
    start_date: string;
    end_date: string;
    visit_occurrence_id: number;
    source_value: string;
    visit_type_concept_id: number;
    visit_type: string;
    // Medication-specific fields
    days_supply?: number | null;
    refills?: number | null;
    sig?: string | null;
    route_name?: string | null;
    provider_name?: string | null;
    // Condition-specific fields
    condition_status?: string | null;
    stop_reason?: string | null;
    // Procedure-specific fields
    modifier_name?: string | null;
}

// Visit Details Response - Related clinical data for a specific visit
export interface VisitConditionRecord {
    condition_occurrence_id: number;
    concept_name: string | null;
    condition_start_date: string | null;
    condition_source_value: string | null;
}

export interface VisitMedicationRecord {
    drug_exposure_id: number;
    concept_name: string | null;
    drug_exposure_start_date: string | null;
    drug_source_value: string | null;
    sig: string | null;
}

export interface VisitProcedureRecord {
    procedure_occurrence_id: number;
    concept_name: string | null;
    procedure_date: string | null;
    procedure_source_value: string | null;
}

export interface VisitNoteRecord {
    note_id: number;
    note_title: string | null;
    note_date: string | null;
    note_class: string | null;
}

export interface VisitDetailsResponse {
    conditions: VisitConditionRecord[];
    medications: VisitMedicationRecord[];
    procedures: VisitProcedureRecord[];
    notes: VisitNoteRecord[];
}
